import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getRegret, likeRegret } from '../api';
import { Regret, CATEGORY_COLORS, CATEGORY_LABELS } from '../types';

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals: [number, string][] = [
    [31536000, 'year'],
    [2592000, 'month'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute'],
  ];

  for (const [secondsInInterval, label] of intervals) {
    const interval = Math.floor(seconds / secondsInInterval);
    if (interval >= 1) {
      return `${interval} ${label}${interval > 1 ? 's' : ''} ago`;
    }
  }

  return 'just now';
}

export default function RegretPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get('success') === 'true';

  const [regret, setRegret] = useState<Regret | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [isLiking, setIsLiking] = useState(false);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);

    getRegret(id)
      .then(data => {
        setRegret(data);
        setLikes(data.likes);
      })
      .catch(() => {
        setError('Regret not found');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const handleLike = async () => {
    if (!regret || hasLiked || isLiking) return;

    setIsLiking(true);
    try {
      const result = await likeRegret(regret.id);
      setLikes(result.likes);
      setHasLiked(true);
    } catch {
      // Silently fail
    } finally {
      setIsLiking(false);
    }
  };

  const handleShare = async () => {
    if (!regret) return;

    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Wall of Regrets',
          text: `"${regret.text}"`,
          url,
        });
      } catch {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="animate-pulse-soft text-gray-500 text-xl">Loading...</div>
      </div>
    );
  }

  if (error || !regret) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4">
        <p className="text-gray-500 text-xl">{error || 'Regret not found'}</p>
        <Link to="/" className="btn btn-primary">
          Back to the Wall
        </Link>
      </div>
    );
  }

  const bgColor = CATEGORY_COLORS[regret.category_tag];
  const categoryLabel = CATEGORY_LABELS[regret.category_tag];
  const timeAgo = formatTimeAgo(regret.submitted_at);

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 py-12">
      {/* Success message */}
      {isSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-xl mb-8 max-w-md text-center">
          <p className="font-medium">Your regret has been posted!</p>
          <p className="text-sm mt-1 text-green-600">
            It's now permanently on the wall. Share it or keep it private — it's up to you.
          </p>
        </div>
      )}

      {/* Main regret card */}
      <div className={`${bgColor} rounded-2xl p-8 md:p-12 max-w-2xl w-full shadow-lg`}>
        <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-relaxed text-gray-800 text-center">
          "{regret.text}"
        </p>

        {/* Metadata */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-gray-600">
          <span className="px-3 py-1 rounded-full bg-black/5 text-sm font-medium">
            {categoryLabel}
          </span>
          <span className="text-sm">{timeAgo}</span>
        </div>

        {/* Actions */}
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            onClick={handleLike}
            disabled={hasLiked || isLiking}
            className={`
              flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-all
              ${hasLiked
                ? 'bg-red-100 text-red-600'
                : 'bg-white text-gray-600 hover:bg-black/5'
              }
            `}
          >
            <svg
              className={`w-5 h-5 ${hasLiked ? 'fill-current' : ''}`}
              fill={hasLiked ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            {likes}
          </button>

          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-gray-600 hover:bg-black/5 font-medium transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            Share
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-8 flex items-center gap-4">
        <Link to="/" className="btn btn-secondary">
          Back to the Wall
        </Link>
        <Link to="/random" className="btn btn-secondary">
          Random Regret
        </Link>
      </div>

      {/* Submit CTA */}
      <p className="mt-8 text-gray-500">
        Want to share yours?{' '}
        <Link to="/submit" className="text-gray-900 font-medium hover:underline">
          Add a regret — $1
        </Link>
      </p>
    </div>
  );
}
